import type { MfaStatus } from "./data/types";

/**
 * When the 6 digit code is asked (owner's decisions, 2026-11-02 and 2026-11-04).
 *   - Without the tick: the code is asked again whenever the browser (the tab) is opened again
 *     (a mark in sessionStorage) or the user signs in again.
 *   - With the tick "Ezen az eszközön 30 napig ne kérdezze": the server remembers this device for
 *     30 days (20261223000000_mfa_trusted_device.sql). The browser keeps the device secret it got
 *     once (localStorage, per user) and sends it with every request (header x-garagevo-device);
 *     the server then counts the second step as done even after a new password sign in. Before the
 *     code screen the app asks the server whether the secret is still valid (trusted_device_ok);
 *     if not, the secret is dropped and the code is asked.
 * Signing out keeps the device remembered (that is the point); "Kijelentkezés és eszköz
 * elfelejtése" and Settings forget it. Storage may be blocked (private window): then the code is
 * simply asked more often.
 */
export const MFA_TRUST_DAYS = 30;
export const DEVICE_HEADER = "x-garagevo-device";

const deviceKey = (userId: string) => `garagevo.mfaDevice.${userId}`;
const sessionKey = (userId: string) => `garagevo.mfaSession.${userId}`;
/** The old browser only mark (before 2026-11-04): never trusted again, removed when seen. */
const legacyKey = (userId: string) => `garagevo.mfaTrust.${userId}`;

function read(storage: () => Storage | undefined, key: string): string | null {
  try {
    return storage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}
function write(storage: () => Storage | undefined, key: string, value: string | null) {
  try {
    if (value === null) storage()?.removeItem(key);
    else storage()?.setItem(key, value);
  } catch {
    // blocked: the code is asked again next time
  }
}
const local = () => globalThis.localStorage;
const session = () => globalThis.sessionStorage;

/** The device secret this browser keeps for the user (64 hex characters), or null. */
export function deviceSecret(userId: string): string | null {
  const s = read(local, deviceKey(userId));
  return s && /^[0-9a-f]{64}$/.test(s) ? s : null;
}
export function saveDeviceSecret(userId: string, secret: string) {
  write(local, legacyKey(userId), null);
  write(local, deviceKey(userId), secret);
}
/** The device is no longer remembered for the user (on this browser). */
export function dropDeviceSecret(userId: string) {
  write(local, legacyKey(userId), null);
  write(local, deviceKey(userId), null);
}

/**
 * Whether the code screen must come now. `deviceOk`: the server confirmed this device's secret
 * for the user (trusted_device_ok).
 */
export function codeNeeded(userId: string, status: MfaStatus, deviceOk = false): boolean {
  if (!status.factor) return false;
  if (deviceOk) return false;
  if (!status.verified) return true;
  return read(session, sessionKey(userId)) !== "1";
}

/** After the right code (or setting it up): not asked again in this tab. */
export function rememberCode(userId: string) {
  write(session, sessionKey(userId), "1");
}

/** Two-step sign in switched off, reset or the user forgot this device: nothing is remembered here. */
export function forgetDevice(userId: string) {
  write(session, sessionKey(userId), null);
  dropDeviceSecret(userId);
}

/** The signed in user's id from a request's "Authorization: Bearer <token>" (null for the publishable key). */
export function userIdFromAuthorization(header: string | null | undefined): string | null {
  const token = /^Bearer\s+(.+)$/i.exec(header ?? "")?.[1];
  const payload = token?.split(".")[1];
  if (!payload) return null;
  try {
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(payload.length / 4) * 4, "="))) as { sub?: unknown; role?: unknown };
    return typeof json.sub === "string" && json.role === "authenticated" ? json.sub : null;
  } catch {
    return null;
  }
}

/**
 * A short name of this browser and system for the list of remembered devices (e.g. "Safari, iPhone").
 * Product names only, so it needs no translation.
 */
export function deviceLabel(ua = globalThis.navigator?.userAgent ?? ""): string {
  const browser = /Edg\//.test(ua) ? "Edge"
    : /OPR\/|Opera/.test(ua) ? "Opera"
    : /SamsungBrowser/.test(ua) ? "Samsung Internet"
    : /Firefox\/|FxiOS/.test(ua) ? "Firefox"
    : /Chrome\/|CriOS/.test(ua) ? "Chrome"
    : /Safari\//.test(ua) ? "Safari"
    : "";
  const system = /iPhone/.test(ua) ? "iPhone"
    : /iPad/.test(ua) ? "iPad"
    : /Android/.test(ua) ? "Android"
    : /Windows/.test(ua) ? "Windows"
    : /Mac OS X|Macintosh/.test(ua) ? "Mac"
    : /CrOS/.test(ua) ? "ChromeOS"
    : /Linux/.test(ua) ? "Linux"
    : "";
  return [browser, system].filter(Boolean).join(", ") || ua.slice(0, 60);
}
